import React from 'react'
import styled from 'styled-components'
import Container from '@material-ui/core/Container'
import {MenuIconButton} from 'lib/ui/IconButton/MenuIconButton'
import {eventRoutes} from 'Event/Routes'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {rgba} from 'lib/color'
import {SimpleBlog, useSimpleBlogTemplate} from 'Event/template/SimpleBlog'
import {useToggle} from 'lib/toggle'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import {SimpleBlogConfig} from 'Event/template/SimpleBlog/SimpleBlogConfig'
import {useEditMode} from 'Event/EditModeProvider'
import {useAttendeeVariables} from 'Event'
import {Font, fontStyles, useLoadFont} from 'lib/FontSelect'

export default function Header(props: {
  toggleMenu: () => void
  menuVisible: boolean
  'aria-label'?: string
}) {
  const {flag: configVisible, toggle: toggleConfig} = useToggle()
  const template = useSimpleBlogTemplate()
  const {menu, header, textColor} = template
  const {iconColor} = menu
  const isEditMode = useEditMode()
  useLoadFont(header.textFont)
  const v = useAttendeeVariables()

  const content = (
    <CollapsableBackground>
      <CollapsableColorOverlay>
        <Container maxWidth="lg">
          <Layout>
            <Side>
              <MenuIconButton
                active={props.menuVisible}
                onClick={props.toggleMenu}
                aria-label="show side menu"
                iconColor={iconColor}
              />
            </Side>
            <Middle>
              <LogoLink to={eventRoutes.root} disableStyles>
                <CollapsableLogo />
              </LogoLink>
              {header.isEnabled && header.text && (
                <HeaderText
                  aria-label="header text"
                  font={header.textFont ?? null}
                  textColor={header.textColor ?? textColor ?? '#000000'}
                  fontSize={header.textFontSize ?? 24}
                >
                  {v(header.text)}
                </HeaderText>
              )}
            </Middle>
            <Side />
          </Layout>
        </Container>
      </CollapsableColorOverlay>
    </CollapsableBackground>
  )

  if (!isEditMode) {
    return content
  }

  return (
    <>
      <SimpleBlogConfig showing={configVisible} onClose={toggleConfig} />
      <Editable onEdit={toggleConfig}>{content}</Editable>
    </>
  )
}

function CollapsableBackground(props: {children: React.ReactElement}) {
  const template = useSimpleBlogTemplate()
  const {header} = template
  const backgroundImage = header.isEnabled ? header.background : ''

  return (
    <Box
      backgroundImage={backgroundImage}
      backgroundPosition={header.backgroundPosition}
      shadowEnabled={header.shadowEnabled}
      isEnabled={header.isEnabled}
    >
      {props.children}
    </Box>
  )
}

function CollapsableLogo() {
  const template = useSimpleBlogTemplate()
  const {title, header} = template
  const logo = template.logo ?? ''

  if (!header.isEnabled || !logo) {
    return null
  }
  return (
    <Logo
      src={logo}
      alt={title}
      height={header.logoHeight}
      max={header.height}
      aria-label="logo"
    />
  )
}

function Layout(props: {children: React.ReactElement | React.ReactElement[]}) {
  const template = useSimpleBlogTemplate()
  const {header, logo} = template
  const isEditMode = useEditMode()

  const height = header.isEnabled ? header.height : 50
  const mobileHeight = Math.round(height * 0.7)

  // The configured height frames the logo, so text alone should not reserve it.
  const showingText = header.isEnabled && Boolean(header.text)
  const reserveHeight = Boolean(logo) || !showingText

  return (
    <LayoutBox
      desktopHeight={height}
      mobileHeight={mobileHeight}
      reserveHeight={reserveHeight}
      isEnabled={header.isEnabled}
      isEditMode={isEditMode}
      arial-label="header layout"
    >
      {props.children}
    </LayoutBox>
  )
}

function CollapsableColorOverlay(props: {children: React.ReactElement}) {
  const template = useSimpleBlogTemplate()
  const {header} = template
  const backgroundColorRgb = rgba(
    header.backgroundColor,
    header.backgroundOpacity / 100,
  )

  if (!header.isEnabled) {
    return props.children
  }

  return (
    <ColorOverlay color={backgroundColorRgb} aria-label="header">
      {props.children}
    </ColorOverlay>
  )
}

const LayoutBox = styled.div<{
  desktopHeight: number
  mobileHeight: number
  reserveHeight: boolean
  isEnabled?: boolean
  isEditMode?: boolean
}>`
  ${(props) =>
    props.reserveHeight ? `min-height: ${props.mobileHeight}px;` : ''}
  display: flex;
  ${(props) =>
    !props.isEnabled && !props.isEditMode ? 'position: absolute;' : ''}

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    ${(props) =>
      props.reserveHeight ? `min-height: ${props.desktopHeight}px;` : ''}
  }
`

const Side = styled.div`
  width: 42px;
  padding: ${(props) => props.theme.spacing[6]} 0;
`

const Box = styled.div<{
  backgroundImage: string | null
  shadowEnabled?: boolean
  isEnabled?: boolean
  backgroundPosition?: SimpleBlog['header']['backgroundPosition']
}>`
  ${(props) =>
    props.backgroundImage
      ? `background-image: url(${props.backgroundImage});`
      : null}

  background-size: cover;
  background-position: ${(props) => props.backgroundPosition};
  background-repeat: no-repeat;
  margin-bottom: ${(props) =>
    props.isEnabled ? props.theme.spacing[8] : 'unset'};
  ${(props) =>
    props.shadowEnabled && props.isEnabled
      ? 'box-shadow: 20px 20px 50px #ddd;'
      : ''}
`

const ColorOverlay = styled.div<{
  color: string
}>`
  background-color: ${(props) => props.color};
`

const Middle = styled.div`
  flex: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing[2]};
`

const HeaderText = styled.div<{
  font: Font | null
  textColor: string
  fontSize: number
}>`
  ${(props) => fontStyles(props.font)}
  color: ${(props) => props.textColor};
  font-size: ${(props) => props.fontSize}px;
  line-height: 1.5;
  text-align: center;
  padding: 0 ${(props) => props.theme.spacing[4]};
  word-break: break-word;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    line-height: 2;
  }
`

const LogoLink = styled(RelativeLink)`
  display: contents;
`
const Logo = styled.img<{
  height: number
  max: number
}>`
  height: ${(props) => props.height}px;
  max-height: ${(props) => props.max - 40}px;
  max-width: 100%;
  object-fit: contain;
`
